/**
 * AdminOnboardingSection - Search a user and edit their onboarding state
 *
 * Allows admins to search a user by email/name and view/edit their
 * onboarding track and completed steps.
 *
 * Invite management lives in AdminInvitesSection.
 *
 * @atomic @idempotent
 */
import { memo, useState, useCallback } from 'react'
import { adminAPI } from '../../services/api'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import { toast } from '../../services/toastService'
import UserOnboardingEditor from './UserOnboardingEditor'

const AdminOnboardingSection = memo(function AdminOnboardingSection({ onBack }) {
  const { execute, loading } = useAsyncOperation()
  const [userSearch, setUserSearch] = useState('')
  const [foundUser, setFoundUser] = useState(null)

  const handleSearchUser = useCallback(async (e) => {
    e.preventDefault()
    if (!userSearch.trim()) return
    await execute(() => adminAPI.getUsers({ search: userSearch, limit: 5 }), {
      onSuccess: (r) => {
        const users = r.data?.data ?? []
        if (users.length === 0) {
          toast.error('No user found')
          setFoundUser(null)
        } else {
          setFoundUser(users[0])
        }
      },
      onError: () => toast.error('User search failed'),
    })
  }, [userSearch, execute])

  return (
    <div className="space-y-4 border-t pt-6">
      <h3 className="text-sm font-semibold text-gray-700">Edit User Onboarding State</h3>
      <form onSubmit={handleSearchUser} className="flex gap-2">
        <input
          type="text"
          value={userSearch}
          onChange={e => setUserSearch(e.target.value)}
          placeholder="Search by email or name…"
          className="flex-1 text-sm border border-gray-200 rounded px-3 py-2"
        />
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 text-sm bg-primary text-white rounded hover:bg-primary/90 transition-colors disabled:opacity-50"
        >
          Search
        </button>
      </form>
      {foundUser && (
        <UserOnboardingEditor userId={foundUser.id} email={foundUser.email} />
      )}
    </div>
  )
})

export default AdminOnboardingSection
